import api from './api'
import { User } from '@store/authStore'

export interface LoginCredentials {
  username: string
  password: string
  remember_me?: boolean
}

export interface AuthResponse {
  success: boolean
  user: User
  token: string
  message?: string
}

export interface GoogleAuthResponse {
  authorization_url: string
  state: string
}

export interface CheckEmailResponse {
  exists: boolean
  has_password: boolean
  is_google_user: boolean
  two_factor_enabled: boolean
}

export interface SendOTPResponse {
  success: boolean
  message: string
}

export interface VerifyOTPResponse {
  success: boolean
  message: string
  email_verified: boolean
}

export interface EmailLoginResponse {
  success: boolean
  message: string
  user?: User
  token?: string
  requires_2fa?: boolean
}

export interface CheckUsernameResponse {
  available: boolean
  message: string
}

export const authService = {
  // Check authentication status
  checkAuth: async (): Promise<{ authenticated: boolean; user?: User }> => {
    const response = await api.get('/api/auth/check-auth')
    return response.data
  },

  // Login with username/password (legacy)
  login: async (credentials: LoginCredentials): Promise<AuthResponse> => {
    const response = await api.post('/api/auth/login', credentials)
    return response.data
  },

  // Logout
  logout: async (): Promise<void> => {
    await api.post('/api/auth/logout')
  },

  // Get Google OAuth URL
  getGoogleAuthUrl: async (): Promise<GoogleAuthResponse> => {
    const response = await api.get('/api/auth/google-login')
    return response.data
  },

  // Update user profile
  updateProfile: async (data: {
    full_name?: string
    email?: string
    phone_number?: string
    country_code?: string
    preferred_language?: string
    content_language?: string
    response_level?: string
  }): Promise<AuthResponse> => {
    const response = await api.post('/api/auth/update-profile', data)
    return response.data
  },

  // Change password
  changePassword: async (data: {
    current_password?: string
    new_password: string
  }): Promise<{ success: boolean; message: string }> => {
    const response = await api.post('/api/auth/change-password', data)
    return response.data
  },

  // Get supported languages
  getSupportedLanguages: async (): Promise<{ languages: string[] }> => {
    const response = await api.get('/api/auth/languages')
    return response.data
  },

  // Sync wallet balance from server
  syncWalletBalance: async (): Promise<number> => {
    const response = await api.get('/api/payment/wallet/balance')
    return response.data.balance
  },

  // Delete user account
  deleteAccount: async (): Promise<{ success: boolean; message: string }> => {
    const response = await api.delete('/api/auth/delete-account')
    return response.data
  },

  // Check if email exists
  checkEmail: async (email: string): Promise<CheckEmailResponse> => {
    const response = await api.post('/api/auth/check-email', { email })
    return response.data
  },

  // Send email OTP for verification
  sendEmailOTP: async (email: string, purpose: 'verify' | '2fa' = 'verify'): Promise<SendOTPResponse> => {
    const response = await api.post('/api/auth/send-email-otp', { email, purpose })
    return response.data
  },

  // Verify email OTP (for signup)
  verifyEmailOTP: async (email: string, otp_code: string): Promise<VerifyOTPResponse> => {
    const response = await api.post('/api/auth/verify-email-otp', { email, otp_code })
    return response.data
  },

  // Register new user with username and password
  register: async (data: {
    email: string
    username: string
    password: string
    full_name?: string
    referral_code?: string
  }): Promise<AuthResponse> => {
    const response = await api.post('/api/auth/register', data)
    return response.data
  },

  // Email login with password
  emailLogin: async (email: string, password: string): Promise<EmailLoginResponse> => {
    const response = await api.post('/api/auth/email-login', { email, password })
    return response.data
  },

  // Verify 2FA OTP
  verify2FA: async (email: string, otp_code: string): Promise<AuthResponse> => {
    const response = await api.post('/api/auth/verify-2fa', { email, otp_code })
    return response.data
  },

  // Check if username is available
  checkUsername: async (username: string): Promise<CheckUsernameResponse> => {
    const response = await api.post('/api/auth/check-username', { username })
    return response.data
  },

  // Set password for existing user (after OTP verification)
  setPassword: async (email: string, password: string): Promise<AuthResponse> => {
    const response = await api.post('/api/auth/set-password', { email, password })
    return response.data
  },

  // Send OTP for enabling/disabling 2FA
  send2FAOTP: async (): Promise<SendOTPResponse> => {
    const response = await api.post('/api/auth/send-2fa-otp')
    return response.data
  },

  // Toggle 2FA (enable/disable) after OTP verification
  toggle2FA: async (otp_code: string, enable: boolean): Promise<{ success: boolean; message: string; two_factor_enabled: boolean }> => {
    const response = await api.post('/api/auth/toggle-2fa', { otp_code, enable })
    return response.data
  },

  // ==================== Phone Number Login/Signup ====================

  // Check if phone number exists
  checkPhone: async (phone_number: string, country_code: string = '91'): Promise<{
    exists: boolean
    has_password: boolean
    is_google_user: boolean
    has_email: boolean
  }> => {
    const response = await api.post('/api/auth/check-phone', { phone_number, country_code })
    return response.data
  },

  // Send OTP to phone number
  sendPhoneOTP: async (phone_number: string, country_code: string = '91'): Promise<{
    success: boolean
    message: string
    is_new_user: boolean
    timeout: number
  }> => {
    const response = await api.post('/api/auth/send-phone-otp', { phone_number, country_code })
    return response.data
  },

  // Verify phone OTP
  verifyPhoneOTP: async (phone_number: string, otp_code: string): Promise<{
    success: boolean
    message: string
    phone_verified: boolean
    is_new_user: boolean
  }> => {
    const response = await api.post('/api/auth/verify-phone-otp', { phone_number, otp_code })
    return response.data
  },

  // Register with phone number
  phoneRegister: async (data: {
    phone_number: string
    full_name: string
    username: string
    referral_code?: string
  }): Promise<AuthResponse> => {
    const response = await api.post('/api/auth/phone-register', data)
    return response.data
  },

  // Login with phone number
  phoneLogin: async (phone_number: string): Promise<AuthResponse> => {
    const response = await api.post('/api/auth/phone-login', { phone_number })
    return response.data
  },
}
